import { useCallback, useEffect, useState } from 'react';
import type { NotificationTemplates } from '@domain/entities';
import type { NotificationChannel, NotificationType } from '@domain/value-objects';
import { useContainer } from '../providers/container-context';
import { useToast } from '../providers/toast-context';
import { useAsync } from '../hooks/use-async';
import { useIsMobile } from '../hooks/use-is-mobile';
import { EmptyState, ErrorState, RefreshButton, TableSkeleton } from '../components/ui';

const CARDS: readonly (readonly [NotificationType, string])[] = [
  ['offerApproved', 'Offre approuvée'],
  ['offerRejected', 'Offre rejetée'],
  ['articlePublished', 'Article publié'],
];

const CHANNELS: readonly (readonly [NotificationChannel, string])[] = [
  ['email', 'Email'],
  ['app', 'In-app'],
  ['both', 'Les deux'],
];

const TYPE_LABELS: Record<NotificationType, string> = {
  offerApproved: 'Offre approuvée',
  offerRejected: 'Offre rejetée',
  articlePublished: 'Article publié',
};

const CHANNEL_LABELS: Record<NotificationChannel, string> = {
  email: 'Email',
  app: 'In-app',
  both: 'Email + In-app',
};

export function NotificationsPage(): JSX.Element {
  const container = useContainer();
  const toast = useToast();

  const isMobile = useIsMobile();
  const templatesResource = useAsync(() => container.settings.getTemplates.execute(), [container]);
  const logResource = useAsync(() => container.settings.listLog.execute(20), [container]);

  const [templates, setTemplates] = useState<NotificationTemplates | null>(null);

  useEffect(() => {
    if (templatesResource.data) setTemplates(templatesResource.data);
  }, [templatesResource.data]);

  const persist = useCallback(
    async (type: NotificationType, patch: Partial<{ subject: string; body: string; channel: NotificationChannel }>) => {
      try {
        const next = await container.settings.saveTemplate.execute({ type, patch });
        setTemplates(next);
      } catch (error) {
        toast.notifyError(error);
      }
    },
    [container, toast],
  );

  const log = logResource.data ?? [];

  return (
    <>
      <div className="bbm-toolbar" style={{ marginBottom: 12 }}>
        <div className="bbm-toolbar__start">
          <p style={{ fontSize: 13, color: '#8A95A3', margin: 0, maxWidth: 640 }}>
            Quand l'admin approuve ou rejette une offre, une notification est envoyée automatiquement à
            l'entreprise (email, notification dans l'application, ou les deux).
          </p>
        </div>
        <div className="bbm-toolbar__end">
          <RefreshButton onRefresh={templatesResource.reload} loading={templatesResource.loading} />
        </div>
      </div>

      {templatesResource.loading ? <TableSkeleton rows={2} /> : null}
      {templatesResource.error ? (
        <ErrorState
          status={templatesResource.error.status}
          message={templatesResource.error.message}
          onRetry={templatesResource.reload}
        />
      ) : null}

      {templates ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 18,
            marginBottom: 26,
          }}
        >
          {CARDS.map(([type, label]) => {
            const template = templates[type];
            return (
              <section key={type} className="bbm-card" style={{ borderRadius: 14 }}>
                <h2
                  style={{
                    fontFamily: "'Baloo 2', sans-serif",
                    fontSize: 15,
                    margin: '0 0 12px',
                    fontWeight: 700,
                  }}
                >
                  {label}
                </h2>
                <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
                  {CHANNELS.map(([channel, channelLabel]) => (
                    <button
                      key={channel}
                      type="button"
                      onClick={() => void persist(type, { channel })}
                      style={{
                        padding: '6px 13px',
                        borderRadius: 99,
                        border: 'none',
                        fontSize: 12,
                        fontWeight: 700,
                        background: template.channel === channel ? '#33506B' : '#F0F2F5',
                        color: template.channel === channel ? '#fff' : '#6B7785',
                      }}
                    >
                      {channelLabel}
                    </button>
                  ))}
                </div>

                <label className="bbm-label" htmlFor={`subject-${type}`}>
                  Sujet (email)
                </label>
                <input
                  id={`subject-${type}`}
                  className="bbm-field"
                  style={{ marginBottom: 12 }}
                  value={template.subject}
                  onChange={(event) =>
                    setTemplates({
                      ...templates,
                      [type]: { ...template, subject: event.target.value },
                    })
                  }
                  onBlur={(event) => void persist(type, { subject: event.target.value })}
                />

                <label className="bbm-label" htmlFor={`body-${type}`}>
                  Message
                </label>
                <textarea
                  id={`body-${type}`}
                  className="bbm-field"
                  rows={3}
                  style={{ resize: 'vertical' }}
                  value={template.body}
                  onChange={(event) =>
                    setTemplates({
                      ...templates,
                      [type]: { ...template, body: event.target.value },
                    })
                  }
                  onBlur={(event) => void persist(type, { body: event.target.value })}
                />
                <div style={{ fontSize: 11, color: '#8A95A3', marginTop: 6 }}>
                  Variables : {'{entreprise}'}, {'{offre}'}
                </div>
              </section>
            );
          })}
        </div>
      ) : null}

      <div className="bbm-toolbar" style={{ marginBottom: 12 }}>
        <div className="bbm-toolbar__start">
          <h2 style={{ fontFamily: "'Baloo 2', sans-serif", fontSize: 15, margin: 0 }}>
            Journal des notifications envoyées
          </h2>
        </div>
        <div className="bbm-toolbar__end">
          <RefreshButton onRefresh={logResource.reload} loading={logResource.loading} />
        </div>
      </div>

      {log.length === 0 ? (
        <EmptyState
          title="Aucune notification encore"
          body="Approuvez ou rejetez une offre pour voir apparaître une entrée ici."
        />
      ) : isMobile ? (
        <div className="bbm-card-stack">
          {log.map((entry) => (
            <div key={entry.id} className="bbm-mobile-card">
              <div className="bbm-mobile-card__head">
                <div>
                  <div className="bbm-mobile-card__title">{entry.subject}</div>
                  <div className="bbm-mobile-card__sub">{TYPE_LABELS[entry.type]}</div>
                </div>
              </div>
              <div className="bbm-mobile-card__fields">
                <div className="bbm-mobile-card__field">
                  <span className="bbm-mobile-card__label">Canal</span>
                  <span>{CHANNEL_LABELS[entry.channel]}</span>
                </div>
                <div className="bbm-mobile-card__field">
                  <span className="bbm-mobile-card__label">Entreprise</span>
                  <span>{entry.company}</span>
                </div>
                <div className="bbm-mobile-card__field">
                  <span className="bbm-mobile-card__label">Date</span>
                  <span>{entry.sentAt}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bbm-panel">
          <div className="bbm-row bbm-row--head">
            <div style={{ width: 150 }}>Type</div>
            <div style={{ width: 220 }}>Entreprise</div>
            <div style={{ width: 220 }}>Sujet</div>
            <div style={{ width: 140 }}>Canal</div>
            <div style={{ width: 100 }}>Date</div>
          </div>
          {log.map((entry) => (
            <div key={entry.id} className="bbm-row" style={{ padding: '12px 20px' }}>
              <div className="bbm-cell-strong" style={{ width: 150, fontSize: 13 }}>
                {TYPE_LABELS[entry.type]}
              </div>
              <div className="bbm-cell" style={{ width: 220 }}>
                {entry.company}
              </div>
              <div className="bbm-cell bbm-truncate" style={{ width: 220 }}>
                {entry.subject}
              </div>
              <div style={{ width: 140, fontSize: 12.5, color: '#8A95A3' }}>
                {CHANNEL_LABELS[entry.channel]}
              </div>
              <div style={{ width: 100, fontSize: 12.5, color: '#8A95A3' }}>{entry.sentAt}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
